<template>
  <div class="memory-game flex flex-col h-full w-full justify-center items-center">
    <div class="w-full max-w-3xl flex flex-col items-center">
      <div
        id="memory-grid"
        class="grid gap-4 w-full min-w-0 mx-auto"
        :style="{
          aspectRatio: `${cols}/${rows}`,
          maxWidth: `min(100%, calc(60vh * ${cols}/${rows}))`,
          maxHeight: '60vh',
          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        }"
      >
        <button
          v-for="(card, index) in cards"
          :key="index"
          type="button"
          class="relative w-full h-full min-h-0 perspective-1000 cursor-pointer group"
          :disabled="card.matched || lockFlip"
          @click="flip(index)"
        >
          <div
            class="w-full h-full relative preserve-3d transition-transform duration-500 rounded-2xl shadow-sm border border-[#1c4848]/5"
            :class="{ 'rotate-y-180': card.flipped || card.matched }"
          >
            <div class="absolute inset-0 backface-hidden bg-white flex items-center justify-center rounded-2xl group-hover:bg-gray-50 transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-10 h-10 text-[#1c4848]/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
            </div>
            <div class="absolute inset-0 backface-hidden rotate-y-180 bg-[#1c4848] rounded-2xl overflow-hidden ring-4 ring-[#ecd79f]/30">
              <img
                v-if="pairImages[card.pairId]"
                :src="pairImages[card.pairId]"
                class="w-full h-full object-cover"
                alt=""
                @error="($event.target).style.display = 'none'"
              >
            </div>
          </div>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { parseMemoryGridSize } from './parseGameConfig.js';

const props = defineProps({
  game: { type: Object, required: true },
});

const emit = defineEmits(['win', 'stats']);

const config = computed(() => props.game?.config || {});
const grid = computed(() => parseMemoryGridSize(config.value.grid_size));
const cols = computed(() => grid.value.cols);
const rows = computed(() => grid.value.rows);
const total = computed(() => grid.value.total);

const flipSpeedMs = computed(() => {
  const s = config.value.flip_speed;
  if (s === 'fast') return 300;
  if (s === 'slow') return 1000;
  return 500;
});

const winMessage = computed(() => config.value.win_message || 'Mashallah! Great Job!');

const assetUrls = computed(() => {
  const a = props.game?.assets || [];
  return Array.isArray(a) ? a.map((x) => x?.url).filter(Boolean) : [];
});

const pairImages = computed(() => assetUrls.value);

const cards = ref([]);
const lockFlip = ref(false);
const won = ref(false);
const startTime = ref(null);
const elapsedSeconds = ref(0);
const moves = ref(0);
let timerId = null;

const elapsedLabel = computed(() => {
  const s = elapsedSeconds.value;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
});

function sendStats() {
  emit('stats', { moves: moves.value, time: elapsedSeconds.value });
}

function isAdjacent(i, j) {
  const c = cols.value;
  const ri = Math.floor(i / c), ci = i % c;
  const rj = Math.floor(j / c), cj = j % c;
  return (Math.abs(ri - rj) === 1 && ci === cj) || (ri === rj && Math.abs(ci - cj) === 1);
}

function buildDeck() {
  const pairCount = total.value / 2;
  const urls = pairImages.value;
  if (urls.length < pairCount) return;
  const ids = [];
  for (let i = 0; i < pairCount; i++) ids.push(i, i);
  for (let i = ids.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [ids[i], ids[j]] = [ids[j], ids[i]];
  }
  const n = ids.length;
  for (let pass = 0; pass < 20; pass++) {
    let swapped = false;
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        if (ids[i] !== ids[j]) continue;
        if (!isAdjacent(i, j)) continue;
        const k = Math.floor(Math.random() * n);
        if (k === i || isAdjacent(i, k)) continue;
        [ids[i], ids[k]] = [ids[k], ids[i]];
        swapped = true;
        break;
      }
      if (swapped) break;
    }
    if (!swapped) break;
  }
  cards.value = ids.map((pairId) => ({ pairId, flipped: false, matched: false }));
  won.value = false;
  moves.value = 0;
  startTime.value = Date.now();
  elapsedSeconds.value = 0;
  sendStats();
  if (timerId) clearInterval(timerId);
  timerId = setInterval(() => {
    if (won.value) return;
    elapsedSeconds.value = Math.floor((Date.now() - startTime.value) / 1000);
    sendStats();
  }, 1000);
}

function flip(index) {
  if (lockFlip.value || cards.value[index].matched || cards.value[index].flipped) return;
  const flippedIndices = cards.value
    .map((c, i) => (c.flipped && !c.matched ? i : -1))
    .filter((i) => i >= 0);
  if (flippedIndices.length >= 2) return;

  cards.value[index].flipped = true;
  const nowFlipped = cards.value
    .map((c, i) => (c.flipped && !c.matched ? i : -1))
    .filter((i) => i >= 0);

  if (nowFlipped.length === 2) {
    moves.value += 1;
    sendStats();
    const [a, b] = nowFlipped;
    if (cards.value[a].pairId === cards.value[b].pairId) {
      cards.value[a].matched = true;
      cards.value[b].matched = true;
      const allMatched = cards.value.every((c) => c.matched);
      if (allMatched) {
        won.value = true;
        if (timerId) clearInterval(timerId);
        emit('win');
      }
    } else {
      lockFlip.value = true;
      setTimeout(() => {
        cards.value[a].flipped = false;
        cards.value[b].flipped = false;
        lockFlip.value = false;
      }, flipSpeedMs.value);
    }
  }
}

watch(() => [props.game?.id, total.value, pairImages.value.length], () => {
  if (pairImages.value.length >= total.value / 2) buildDeck();
}, { immediate: true });

onBeforeUnmount(() => {
  if (timerId) clearInterval(timerId);
});

defineExpose({ restart: buildDeck });
</script>

<style scoped>
.perspective-1000 { perspective: 1000px; }
.preserve-3d { transform-style: preserve-3d; }
.backface-hidden { backface-visibility: hidden; }
.rotate-y-180 { transform: rotateY(180deg); }
</style>
